import { StructureSelectionManager } from "molstar/lib/mol-plugin-state/manager/structure/selection";
import { OrderedSet } from "molstar/lib/mol-data/int";
import { MolScriptBuilder as MS } from "molstar/lib/mol-script/language/builder";
import { Expression } from "molstar/lib/mol-script/language/expression";
import { PluginUIContext } from "molstar/lib/mol-plugin-ui/context";
import { StructureSelectionQuery } from "molstar/lib/mol-plugin-state/helpers/structure-selection-query";
import { SyncRuntimeContext } from "molstar/lib/mol-task/execution/synchronous";
import { PolymerSequenceWrapper } from "molstar/lib/mol-plugin-ui/sequence/polymer";
import { Sequence } from "molstar/lib/mol-model/sequence/sequence";
import {
  StructureSelection,
  Structure,
  StructureSequence,
} from "molstar/lib/mol-model/structure";
import {
  SelectionResidueModel,
  ResidueSelectionModel,
} from "@services/home/model/home.model";
import { SequenceIdCodeModel } from "@services/common/model/gloabal.model";
import type { Model } from "molstar/lib/mol-model/structure/model/model";
import { STANDARD_RESIDUES } from "@services/common/config/common.config";

function mergeSeqData(data: SequenceIdCodeModel[], chainName: string) {
  const result = [];
  const seqIdCounts: Record<number, number> = {};

  // 首先统计每个 seq_id 出现的次数
  for (const item of data) {
    const id = item.seq_id;
    seqIdCounts[id] = (seqIdCounts[id] || 0) + 1;
  }

  let i = 0;
  while (i < data.length) {
    const current = data[i];

    // 如果当前的 seq_id 是重复的，优先处理并单独输出
    if (seqIdCounts[current.seq_id] > 1) {
      const entry =
        current.icode !== ""
          ? `${chainName}:${current.seq_id}${current.icode}`
          : `${chainName}:${current.seq_id}`;
      result.push(entry);
      i++;
    } else if (current.icode !== "") {
      // 非重复的 seq_id，且 icode 不为空，直接添加到结果数组
      result.push(`${chainName}:${current.seq_id}${current.icode}`);
      i++;
    } else {
      // 处理连续的非重复 seq_id，构建序列
      const startSeq = current.seq_id;
      let endSeq = startSeq;
      i++;
      while (
        i < data.length &&
        data[i].icode === "" &&
        seqIdCounts[data[i].seq_id] === 1 && // 确保不是重复的 seq_id
        data[i].seq_id === endSeq + 1
      ) {
        // 检查是否连续
        endSeq = data[i].seq_id;
        i++;
      }
      if (startSeq === endSeq) {
        result.push(`${chainName}:${startSeq}`);
      } else {
        result.push(`${chainName}:${startSeq}-${endSeq}`);
      }
    }
  }
  return result;
}

export function getSitesRange(selection: StructureSelectionManager) {
  const entries = selection.entries;

  const values = Array.from(entries.values());

  const targetEntry = values.find((entry) => {
    return entry.selection.elements.length > 0;
  });

  if (!targetEntry) {
    return "";
  }

  const elements = targetEntry.selection.elements;
  const arr = elements.map((element) => {
    const atomicHierarchy = element.unit.model.atomicHierarchy;

    let atomIndexs: number[] = [];

    if (OrderedSet.isInterval(element.indices)) {
      const residueAtomIndexs = OrderedSet.toArray(element.indices);
      atomIndexs = residueAtomIndexs;
    } else {
      const residueAtomIndexs: number[] = [];
      for (let i = 0; i < element.indices.length; i++) {
        residueAtomIndexs.push(element.indices[i]);
      }
      atomIndexs = residueAtomIndexs;
    }

    const globalIndices = atomIndexs.map((i) => {
      return element.unit.elements[i];
    });

    const residueIndexs = globalIndices.map((i) => {
      return atomicHierarchy.residueAtomSegments.index[i];
    });
    const chainIndexs = globalIndices.map((i) => {
      return atomicHierarchy.chainAtomSegments.index[i];
    });
    const chainSet = new Set([...chainIndexs]);
    const uniqChainIndexs = [...chainSet];
    const residueSet = new Set([...residueIndexs]);
    const uniqReisueIndexs = [...residueSet];

    const idCodeList = uniqReisueIndexs.map((rI) => {
      const id = atomicHierarchy.residues.auth_seq_id.value(rI);
      const icode = atomicHierarchy.residues.pdbx_PDB_ins_code.value(rI);
      const o: SequenceIdCodeModel = {
        seq_id: id,
        icode,
      };
      return o;
    });

    const chainIds = uniqChainIndexs.map((cI) => {
      return atomicHierarchy.chains.auth_asym_id.value(cI);
    });
    const result = mergeSeqData(idCodeList, chainIds[0]);
    const text = result.join(",");
    return text;
  });

  return arr.join(",");
}

export function groupConsecutiveRanges(arr: SequenceIdCodeModel[]) {
  if (!arr.length) return [];

  const result = [];
  let start = arr[0].seq_id;
  let end = start;

  for (let i = 1; i < arr.length; i++) {
    const current = arr[i].seq_id;

    if (current === end + 1) {
      // 连续
      end = current;
    } else {
      // 不连续，收尾当前段
      result.push(start === end ? [start] : [start, end]);
      start = end = current;
    }
  }

  // 添加最后一段
  result.push(start === end ? [start] : [start, end]);

  return result;
}

export function getLabelSitesRange(selection: StructureSelectionManager) {
  const entries = selection.entries;

  const values = Array.from(entries.values());

  const targetEntry = values.find((entry) => {
    return entry.selection.elements.length > 0;
  });

  if (!targetEntry) {
    return "";
  }

  const elements = targetEntry.selection.elements;
  const arr = elements.map((element) => {
    const atomicHierarchy = element.unit.model.atomicHierarchy;

    let atomIndexs: number[] = [];

    if (OrderedSet.isInterval(element.indices)) {
      const residueAtomIndexs = OrderedSet.toArray(element.indices);
      atomIndexs = residueAtomIndexs;
    } else {
      const residueAtomIndexs: number[] = [];
      for (let i = 0; i < element.indices.length; i++) {
        residueAtomIndexs.push(element.indices[i]);
      }
      atomIndexs = residueAtomIndexs;
    }

    const globalIndices = atomIndexs.map((i) => {
      return element.unit.elements[i];
    });

    const residueIndexs = globalIndices.map((i) => {
      return atomicHierarchy.residueAtomSegments.index[i];
    });
    const chainIndexs = globalIndices.map((i) => {
      return atomicHierarchy.chainAtomSegments.index[i];
    });
    const chainSet = new Set([...chainIndexs]);
    const uniqChainIndexs = [...chainSet];
    const residueSet = new Set([...residueIndexs]);
    const uniqReisueIndexs = [...residueSet];

    const labelIdMapAuth = uniqReisueIndexs.reduce((ac, rI) => {
      const id = atomicHierarchy.residues.auth_seq_id.value(rI);
      const labelId = atomicHierarchy.residues.label_seq_id.value(rI);
      const icode = atomicHierarchy.residues.pdbx_PDB_ins_code.value(rI);
      const o: SequenceIdCodeModel = {
        seq_id: id,
        icode,
      };

      ac[labelId] = o;
      return ac;
    }, {} as Record<number, SequenceIdCodeModel>);

    const idLabelCodeList = uniqReisueIndexs.map((rI) => {
      const id = atomicHierarchy.residues.label_seq_id.value(rI);
      const o: SequenceIdCodeModel = {
        seq_id: id,
        icode: "",
      };
      return o;
    });

    const chainIds = uniqChainIndexs.map((cI) => {
      return atomicHierarchy.chains.auth_asym_id.value(cI);
    });

    const ranges = groupConsecutiveRanges(idLabelCodeList);
    const result = ranges.map((range) => {
      if (range.length === 1) {
        const [labelId] = range;
        const authItem = labelIdMapAuth[labelId];

        return `${chainIds[0]}:${authItem.seq_id}${authItem.icode}`;
      } else {
        const [startLabelId, endLabelId] = range;
        const startAuthItem = labelIdMapAuth[startLabelId];
        const endAuthItem = labelIdMapAuth[endLabelId];
        return `${chainIds[0]}:${startAuthItem.seq_id}${startAuthItem.icode}-${endAuthItem.seq_id}${endAuthItem.icode}`;
      }
    });

    return result.join(",");
  });

  return arr.join(",");
}

export function getChainSeqIndexMap(selection: StructureSelectionManager) {
  const entries = selection.entries;
  const values = Array.from(entries.values());
  const targetEntry = values.find((entry) => {
    return entry.selection.elements.length > 0;
  });
  if (!targetEntry || !targetEntry.structure?.model) {
    return undefined;
  }
  const model = targetEntry.structure.model;

  const sequences = model.sequence.sequences;
  const entitySequenceMap = sequences.reduce((ac, item) => {
    ac[item.entityId] = item;
    return ac;
  }, {} as Record<string, StructureSequence.Entity>);

  const elements = targetEntry.selection.elements;
  const map = elements.reduce((ac, element) => {
    const atomicHierarchy = element.unit.model.atomicHierarchy;

    let atomIndexs: number[] = [];

    if (OrderedSet.isInterval(element.indices)) {
      const residueAtomIndexs = OrderedSet.toArray(element.indices);
      atomIndexs = residueAtomIndexs;
    } else {
      const residueAtomIndexs: number[] = [];
      for (let i = 0; i < element.indices.length; i++) {
        residueAtomIndexs.push(element.indices[i]);
      }
      atomIndexs = residueAtomIndexs;
    }

    const globalIndices = atomIndexs.map((i) => {
      return element.unit.elements[i];
    });

    const residueIndexs = globalIndices.map((i) => {
      return atomicHierarchy.residueAtomSegments.index[i];
    });
    const chainIndexs = globalIndices.map((i) => {
      return atomicHierarchy.chainAtomSegments.index[i];
    });
    const chainSet = new Set([...chainIndexs]);
    const uniqChainIndexs = [...chainSet];
    const residueSet = new Set([...residueIndexs]);
    const uniqReisueIndexs = [...residueSet];

    const labelSeqIds = uniqReisueIndexs.map((rI) => {
      const id = atomicHierarchy.residues.label_seq_id.value(rI);

      return id;
    });

    const chainIds = uniqChainIndexs.map((cI) => {
      return atomicHierarchy.chains.label_entity_id.value(cI);
    });

    const labelAsymIds = uniqChainIndexs.map((cI) => {
      return atomicHierarchy.chains.auth_asym_id.value(cI);
    });

    const sequence = entitySequenceMap[chainIds[0]];

    if (sequence) {
      const positionIndexs = labelSeqIds.map((seqId) => {
        const seqPosition = sequence.sequence.index(seqId);

        return seqPosition;
      });

      ac[labelAsymIds[0]] = positionIndexs;

      return ac;
    } else {
      return ac;
    }
  }, {} as Record<string, number[]>);

  return map;
}

export function getChainSequenceMap(selection: StructureSelectionManager) {
  const entries = selection.entries;
  const values = Array.from(entries.values());
  const targetEntry = values.find((entry) => {
    return entry.selection.elements.length > 0;
  });
  if (!targetEntry || !targetEntry.structure?.model) {
    return undefined;
  }
  const model = targetEntry.structure.model;

  const sequences = model.sequence.sequences;
  const entitySequenceMap = sequences.reduce((ac, item) => {
    ac[item.entityId] = item;
    return ac;
  }, {} as Record<string, StructureSequence.Entity>);

  const elements = targetEntry.selection.elements;
  const map = elements.reduce((ac, element) => {
    const atomicHierarchy = element.unit.model.atomicHierarchy;

    let atomIndexs: number[] = [];

    if (OrderedSet.isInterval(element.indices)) {
      const residueAtomIndexs = OrderedSet.toArray(element.indices);
      atomIndexs = residueAtomIndexs;
    } else {
      const residueAtomIndexs: number[] = [];
      for (let i = 0; i < element.indices.length; i++) {
        residueAtomIndexs.push(element.indices[i]);
      }
      atomIndexs = residueAtomIndexs;
    }

    const globalIndices = atomIndexs.map((i) => {
      return element.unit.elements[i];
    });

    const residueIndexs = globalIndices.map((i) => {
      return atomicHierarchy.residueAtomSegments.index[i];
    });
    const chainIndexs = globalIndices.map((i) => {
      return atomicHierarchy.chainAtomSegments.index[i];
    });
    const chainSet = new Set([...chainIndexs]);
    const uniqChainIndexs = [...chainSet];
    const residueSet = new Set([...residueIndexs]);
    const uniqReisueIndexs = [...residueSet];

    const labelSeqIds = uniqReisueIndexs.map((rI) => {
      const id = atomicHierarchy.residues.label_seq_id.value(rI);

      return id;
    });

    const chainIds = uniqChainIndexs.map((cI) => {
      return atomicHierarchy.chains.label_entity_id.value(cI);
    });

    const labelAsymIds = uniqChainIndexs.map((cI) => {
      return atomicHierarchy.chains.auth_asym_id.value(cI);
    });

    const sequence = entitySequenceMap[chainIds[0]];

    if (sequence) {
      const seqs = labelSeqIds.map((seqId) => {
        const seqPosition = sequence.sequence.index(seqId);

        const seqName = sequence.sequence.label.value(seqPosition);

        return seqName;
      });

      ac[labelAsymIds[0]] = seqs;

      return ac;
    } else {
      return ac;
    }
  }, {} as Record<string, string[]>);

  return map;
}

export function combineSequenceAndIndex(
  indexMap?: Record<string, number[]>,
  seqMap?: Record<string, string[]>
) {
  if (!indexMap || !seqMap) {
    return null;
  }
  const chains = Object.keys(indexMap);

  const map = chains.reduce((ac, k) => {
    const indexs = indexMap[k];

    const seqs = seqMap[k];

    ac[k] = indexs.map((p, i) => {
      const o: SelectionResidueModel = {
        amino_acid: seqs[i],
        index: p,
      };

      return o;
    });

    return ac;
  }, {} as Record<string, SelectionResidueModel[]>);

  return map;
}

export function getAllSequenceAndIndex(wrappers: PolymerSequenceWrapper[]) {
  const seqAndIdxMap = wrappers.reduce((ac, wrapper) => {
    const items: SelectionResidueModel[] = [];
    for (let i = 0, il = wrapper.length; i < il; ++i) {
      const label = wrapper.residueLabel(i);
      items.push({
        amino_acid: label,
        index: i,
      });
    }
    if (items.length > 0) {
      ac[wrapper.asymId] = items;
    }
    return ac;
  }, {} as Record<string, SelectionResidueModel[]>);

  return seqAndIdxMap;
}

export function mergeLabelSeqData(nums: number[], chainName: string) {
  const result: string[] = [];
  let start = nums[0];

  for (let i = 1; i <= nums.length; i++) {
    // 如果当前数字不是前一个数字 + 1，说明断开了
    if (nums[i] !== nums[i - 1] + 1) {
      const end = nums[i - 1];
      result.push(
        start === end ? `${chainName}:${start}` : `${chainName}:${start}-${end}`
      );
      start = nums[i];
    }
  }

  return result;
}

export function getAllSitesLabelRange(wrappers: PolymerSequenceWrapper[]) {
  const list = wrappers
    .map((wrapper) => {
      const seqIds: number[] = [];
      for (let i = 0, il = wrapper.length; i < il; ++i) {
        const seqId = wrapper.seqId(i);
        seqIds.push(seqId);
      }
      const result = mergeLabelSeqData(seqIds, wrapper.asymId);
      return result;
    })
    .filter((arr) => {
      return arr.length > 0;
    })
    .map((result) => {
      const text = result.join(",");
      return text;
    });
  return list.join(",");
}

export function splitByIndexGap(data: SelectionResidueModel[]) {
  if (!data.length) return [];

  const result = [];
  let currentGroup = [data[0]];

  for (let i = 1; i < data.length; i++) {
    if (data[i].index - data[i - 1].index > 1) {
      // 差大于 1，保存当前组，开启新组
      result.push(currentGroup);
      currentGroup = [];
    }
    currentGroup.push(data[i]);
  }

  // 别忘了最后一组要加进去
  if (currentGroup.length) result.push(currentGroup);

  return result;
}

export function getSequenceArr(
  antigenMap?: Record<string, SelectionResidueModel[]>,
  epitopeMap?: Record<string, SelectionResidueModel[]>
) {
  if (!antigenMap || !epitopeMap) {
    return [];
  }

  const refKeys = Object.keys(epitopeMap);

  const refMap = refKeys.reduce((ac, k) => {
    ac[k] = epitopeMap[k].reduce((a, item) => {
      a[item.index] = item.amino_acid;
      return a;
    }, {} as Record<number, string>);
    return ac;
  }, {} as Record<string, Record<number, string>>);

  const benchmarkKeys = Object.keys(antigenMap);

  const benchmarkMap = benchmarkKeys.reduce((ac, k) => {
    ac[k] = antigenMap[k].map((item) => {
      const chainData = refMap[k];

      const o = { ...item };
      o.hasColor = chainData && chainData[item.index] ? true : false;

      return o;
    });
    return ac;
  }, {} as Record<string, SelectionResidueModel[]>);

  const keys = Object.keys(benchmarkMap);

  const result = keys.map((k) => {
    const arr = benchmarkMap[k];

    return splitByIndexGap(arr);
  });

  return result;
}

export function parseResidueSelection(selection: string) {
  const entries: ResidueSelectionModel[] = [];
  const parts = selection.split(",");

  for (const part of parts) {
    const [chainId, rest] = part.split(":");

    if (rest.includes("-")) {
      // 处理范围，如 "L:3-20"
      const [start, end] = rest.split("-").map((n) => parseInt(n));
      entries.push({
        kind: "range",
        asym_id: chainId,
        seq_id_beg: start,
        seq_id_end: end,
      });
    } else if (Number.isNaN(+rest)) {
      // 处理带插入码的残基，如 "H:100B"
      const seqId = Number(rest.replace(/[^\d]/g, "")); // 提取数字部分，例如 100
      const insCode = rest.replace(/[\d]/g, ""); // 提取插入码，例如 "B"
      entries.push({
        kind: "single",
        asym_id: chainId,
        seq_id: seqId,
        ins_code: insCode,
      });
    } else {
      // 处理单个残基，如 "H:52"
      entries.push({
        kind: "single",
        asym_id: chainId,
        seq_id: parseInt(rest),
        ins_code: ".", // 无插入码时用 "." 表示
      });
    }
  }

  return entries;
}

export function getSelectionFromChainMap(selector: string, name: string) {
  const arr = parseResidueSelection(selector);

  const expressionList = arr.map((item) => {
    const exp: Expression[] = [
      MS.core.rel.eq([
        MS.struct.atomProperty.macromolecular.label_asym_id(), // 链标识符
        item.asym_id,
      ]),
    ];

    if (item.kind === "range") {
      // 处理范围选择
      exp.push(
        MS.core.rel.inRange([
          MS.struct.atomProperty.macromolecular.label_seq_id(), // 残基编号
          item.seq_id_beg,
          item.seq_id_end,
        ])
      );
    } else if (item.kind === "single") {
      // 处理单个残基选择，包括插入码
      exp.push(
        MS.core.logic.and([
          MS.core.rel.eq([
            MS.struct.atomProperty.macromolecular.label_seq_id(),
            item.seq_id,
          ]),
          MS.core.rel.eq([
            MS.struct.atomProperty.macromolecular.pdbx_PDB_ins_code(),
            item.ins_code === "." ? "" : item.ins_code, // 插入码为空时处理为 ""
          ]),
        ])
      );
    }

    return MS.core.logic.and(exp); // 确保逻辑表达式正确嵌套
  });

  // 将多个条件合并为一个表达式
  const expr = MS.struct.generator.atomGroups({
    "residue-test": MS.core.logic.or(expressionList), // 使用逻辑 OR 连接多个选择条件
  });

  // 创建查询对象
  const query = StructureSelectionQuery(`${name}`, expr);

  return query;
}

export async function selectFromQuery(
  plugin: PluginUIContext,
  query: StructureSelectionQuery,
  structure: Structure
) {
  const runtime = SyncRuntimeContext;
  // 获取选区
  const selection = await query.getSelection(plugin, runtime, structure);

  // 转换为 Loci
  const loci = StructureSelection.toLociWithSourceUnits(selection);

  // 使用 structure selection manager 设置选区
  plugin.managers.structure.selection.fromLoci("set", loci);
}

export function getChainAuthSeqIdMap(selector: string, struct: Structure) {
  const arr = selector.split(",");

  const chainMap = arr.reduce((ac, chainInfo) => {
    const [chainName, authSeqId] = chainInfo.split(":");
    if (!ac[chainName]) {
      ac[chainName] = [+authSeqId];
    } else {
      ac[chainName].push(+authSeqId);
    }

    return ac;
  }, {} as Record<string, number[]>);

  const model = struct.model;

  const sequences = model.sequence.sequences;

  const properties = model.properties;

  const { structAsymMap } = properties;

  const entitySequenceMap = sequences.reduce((ac, item) => {
    ac[item.entityId] = item;
    return ac;
  }, {} as Record<string, StructureSequence.Entity>);

  const keys = Object.keys(chainMap);

  const map = keys.reduce((ac, chainName) => {
    const authIds = chainMap[chainName];
    const entity = structAsymMap.get(chainName);

    const entityId = entity!.entity_id;

    const sequence = entitySequenceMap[entityId];

    const positionIndexs = authIds.map((seqId) => {
      const seqPosition = sequence.sequence.index(seqId);

      return seqPosition;
    });

    ac[chainName] = positionIndexs;

    return ac;
  }, {} as Record<string, number[]>);

  return map;
}

export function getSelectedSequence(selection: StructureSelectionManager) {
  const entries = selection.entries;
  const values = Array.from(entries.values());
  const targetEntry = values.find((entry) => {
    return entry.selection.elements.length > 0;
  });
  if (!targetEntry || !targetEntry.structure?.model) {
    return "";
  }
  const model = targetEntry.structure.model;

  const sequences = model.sequence.sequences;
  const entitySequenceMap = sequences.reduce((ac, item) => {
    ac[item.entityId] = item;
    return ac;
  }, {} as Record<string, StructureSequence.Entity>);

  const elements = targetEntry.selection.elements;
  const arr = elements.map((element) => {
    const atomicHierarchy = element.unit.model.atomicHierarchy;

    let atomIndexs: number[] = [];

    if (OrderedSet.isInterval(element.indices)) {
      const residueAtomIndexs = OrderedSet.toArray(element.indices);
      atomIndexs = residueAtomIndexs;
    } else {
      const residueAtomIndexs: number[] = [];
      for (let i = 0; i < element.indices.length; i++) {
        residueAtomIndexs.push(element.indices[i]);
      }
      atomIndexs = residueAtomIndexs;
    }

    const globalIndices = atomIndexs.map((i) => {
      return element.unit.elements[i];
    });

    const residueIndexs = globalIndices.map((i) => {
      return atomicHierarchy.residueAtomSegments.index[i];
    });
    const chainIndexs = globalIndices.map((i) => {
      return atomicHierarchy.chainAtomSegments.index[i];
    });
    const chainSet = new Set([...chainIndexs]);
    const uniqChainIndexs = [...chainSet];
    const residueSet = new Set([...residueIndexs]);
    const uniqReisueIndexs = [...residueSet];

    const labelSeqIds = uniqReisueIndexs.map((rI) => {
      const id = atomicHierarchy.residues.label_seq_id.value(rI);

      return id;
    });

    const chainIds = uniqChainIndexs.map((cI) => {
      return atomicHierarchy.chains.label_entity_id.value(cI);
    });

    const sequence = entitySequenceMap[chainIds[0]];

    if (sequence) {
      const positionIndexs = labelSeqIds.map((seqId) => {
        const seqPosition = sequence.sequence.index(seqId);
        return seqPosition;
      });

      const labels = positionIndexs.map((idx) => {
        return sequence.sequence.label.value(idx);
      });

      return labels.join("");
    } else {
      return "";
    }
  });

  return arr.join("");
}

export function getSelectedChainSequences(
  selection: StructureSelectionManager
) {
  const entries = selection.entries;
  const values = Array.from(entries.values());
  const targetEntry = values.find(
    (entry) => entry.selection.elements.length > 0
  );
  if (!targetEntry || !targetEntry.structure?.model) {
    return [];
  }
  const model = targetEntry.structure.model;

  // Build map of entityId → sequence object
  const sequences = model.sequence.sequences;
  const entitySequenceMap = sequences.reduce((ac, item) => {
    ac[item.entityId] = item;
    return ac;
  }, {} as Record<string, StructureSequence.Entity>);

  // Collect all chain IDs from the selection
  const elements = targetEntry.selection.elements;
  const chainIds = new Set<string>();

  for (const element of elements) {
    const atomicHierarchy = element.unit.model.atomicHierarchy;

    // Find chain indices of selected atoms
    const atomIndices = OrderedSet.isInterval(element.indices)
      ? OrderedSet.toArray(element.indices)
      : Array.from(element.indices);

    const globalIndices = atomIndices.map((i) => element.unit.elements[i]);
    const chainIndexs = globalIndices.map(
      (i) => atomicHierarchy.chainAtomSegments.index[i]
    );

    // Convert to entity IDs
    chainIndexs.forEach((cI) => {
      const entityId = atomicHierarchy.chains.label_entity_id.value(cI);
      chainIds.add(entityId);
    });
  }

  // Now retrieve full sequences for all selected chains
  const chainSequences: string[] = [];

  for (const entityId of chainIds) {
    const seq = entitySequenceMap[entityId];
    if (seq) {
      // seq.sequence.label is a TypedArray-like with all residues
      const labels: string[] = [];
      for (let i = 0; i < seq.sequence.length; i++) {
        labels.push(seq.sequence.label.value(i));
      }
      chainSequences.push(labels.join(""));
    }
  }

  return chainSequences; // one per selected chain entityId
}

export function getSelectedLabelSequences(wrappers: PolymerSequenceWrapper[]) {
  const list = wrappers.map((wrapper) => {
    const sequence = wrapper.sequence;
    const selectedSeqs: string[] = [];

    for (let i = 0; i < sequence.length; i++) {
      const selected = wrapper.isMissingSelected(i) || wrapper.isSelected(i);

      if (selected) {
        const label = sequence.label.value(i);
        selectedSeqs.push(label.length > 1 ? `(${label})` : label);
      }
    }

    return selectedSeqs.join("");
  });

  return list;
}

export function getMapOfAuthChainToLabelChain(model?: Model) {
  const map: Record<string, string> = {};

  if (!model) {
    return map;
  }

  const { chains } = model.atomicHierarchy;

  for (let i = 0; i < chains._rowCount; i++) {
    const labelId = chains.label_asym_id.value(i);
    const authId = chains.auth_asym_id.value(i);

    if (!map[authId]) {
      map[authId] = labelId;
    }
  }

  return map;
}

export function includeDNAOrRNA(
  sites: string,
  wrappers: PolymerSequenceWrapper[]
) {
  const chains = wrappers
    .filter((wrapper) => {
      return (
        wrapper.sequence.kind === Sequence.Kind.DNA ||
        wrapper.sequence.kind === Sequence.Kind.RNA
      );
    })
    .map((wrapper) => {
      return wrapper.asymId;
    });

  const isDna = chains.some((chain) => {
    return sites.includes(chain);
  });

  return isDna;
}

export function getSelectedFragments(selection: StructureSelectionManager) {
  const entries = selection.entries;
  const values = Array.from(entries.values());
  const targetEntry = values.find((entry) => {
    return entry.selection.elements.length > 0;
  });
  if (!targetEntry || !targetEntry.structure?.model) {
    return [];
  }
  const model = targetEntry.structure.model;

  const sequences = model.sequence.sequences;
  const entitySequenceMap = sequences.reduce((ac, item) => {
    ac[item.entityId] = item;
    return ac;
  }, {} as Record<string, StructureSequence.Entity>);

  const elements = targetEntry.selection.elements;
  const arr = elements.map((element) => {
    const atomicHierarchy = element.unit.model.atomicHierarchy;

    let atomIndexs: number[] = [];

    if (OrderedSet.isInterval(element.indices)) {
      const residueAtomIndexs = OrderedSet.toArray(element.indices);
      atomIndexs = residueAtomIndexs;
    } else {
      const residueAtomIndexs: number[] = [];
      for (let i = 0; i < element.indices.length; i++) {
        residueAtomIndexs.push(element.indices[i]);
      }
      atomIndexs = residueAtomIndexs;
    }

    const globalIndices = atomIndexs.map((i) => {
      return element.unit.elements[i];
    });

    const residueIndexs = globalIndices.map((i) => {
      return atomicHierarchy.residueAtomSegments.index[i];
    });
    const chainIndexs = globalIndices.map((i) => {
      return atomicHierarchy.chainAtomSegments.index[i];
    });
    const chainSet = new Set([...chainIndexs]);
    const uniqChainIndexs = [...chainSet];
    const residueSet = new Set([...residueIndexs]);
    const uniqReisueIndexs = [...residueSet];

    const labelSeqIds = uniqReisueIndexs.map((rI) => {
      const id = atomicHierarchy.residues.label_seq_id.value(rI);

      return id;
    });

    const chainIds = uniqChainIndexs.map((cI) => {
      return atomicHierarchy.chains.label_entity_id.value(cI);
    });

    const sequence = entitySequenceMap[chainIds[0]];

    if (sequence) {
      const positionIndexs = labelSeqIds.map((seqId) => {
        const seqPosition = sequence.sequence.index(seqId);
        return seqPosition;
      });

      const labels = positionIndexs.map((idx) => {
        return sequence.sequence.label.value(idx);
      });

      return labels;
    } else {
      return [];
    }
  });

  return arr;
}

export function getStructureFragments(wrappers: PolymerSequenceWrapper[]) {
  const arr = wrappers.map((wrapper) => {
    const sequence = wrapper.sequence;
    const residues: string[] = [];
    for (let i = 0; i < sequence.length; i++) {
      const label = sequence.label.value(i);
      residues.push(label);
    }
    return residues;
  });

  return arr;
}

export function hasNonStandardResidue(list: string[][]) {
  const residues = list.reduce((ac, item) => {
    return [...ac, ...item];
  }, [] as string[]);

  const hasNonStandard = residues.some((res) => {
    return !STANDARD_RESIDUES.includes(res);
  });

  return hasNonStandard;
}
